import { SubscriptionSummary } from "@/types/businessData";

/**
 * Enum for different subscription lifecycle states
 */
export enum SubscriptionType {
  ACTIVE_PAYING = "active_paying",
  ACTIVE_TRIAL = "active_trial",
  EXPIRED_TRIAL = "expired_trial",
  CANCELED_PAYING = "canceled_paying",
  SCHEDULED_CANCELLATION = "scheduled_cancellation",
  NO_SUBSCRIPTION = "no_subscription",
  PAST_DUE = "past_due",
  INCOMPLETE = "incomplete",
  UNPAID = "unpaid",
  PAUSED = "paused",
}

export interface SubscriptionInfo {
  type: SubscriptionType;
  isActive: boolean;
  hasExpired: boolean;
  wasPaying: boolean;
  isTrialing: boolean;
  needsUpgrade: boolean;
  message: string;
  callToAction: string;
}

/**
 * Central utility function to determine subscription type and status
 * Use this function throughout the app for consistent subscription checks
 */
export function getSubscriptionInfo(
  subscription: SubscriptionSummary | null | undefined
): SubscriptionInfo {
  // No subscription exists
  if (!subscription) {
    return {
      type: SubscriptionType.NO_SUBSCRIPTION,
      isActive: false,
      hasExpired: false,
      wasPaying: false,
      isTrialing: false,
      needsUpgrade: true,
      message: "Ingen aktiv prenumeration.",
      callToAction: "Starta prenumeration",
    };
  }

  const status = subscription.status;
  const currentPeriodEnd = subscription.currentPeriodEnd;
  const trialEnd = subscription.trialEnd;
  const cancelAtPeriodEnd = subscription.cancelAtPeriodEnd;

  // Calculate if subscription has expired
  const hasExpired =
    currentPeriodEnd && new Date(currentPeriodEnd.seconds * 1000) < new Date();

  // Check if currently in trial
  const isCurrentlyTrialing = status === "trialing";

  // Check if they ever paid (after trial ended or no trial at all)
  const wasPaying = (() => {
    if (!trialEnd) return true; // No trial = was/is paying

    const trialEndDate = new Date(trialEnd.seconds * 1000);
    const periodStartDate = new Date(
      subscription.currentPeriodStart.seconds * 1000
    );

    // If current period started after trial ended, they paid at least once
    return periodStartDate > trialEndDate;
  })();

  // Handle different status states
  switch (status) {
    case "active":
      if (cancelAtPeriodEnd) {
        return {
          type: SubscriptionType.SCHEDULED_CANCELLATION,
          isActive: true,
          hasExpired: false,
          wasPaying: true,
          isTrialing: false,
          needsUpgrade: false,
          message: "Din prenumeration kommer att avslutas vid periodens slut.",
          callToAction: "Hantera prenumeration",
        };
      }
      return {
        type: SubscriptionType.ACTIVE_PAYING,
        isActive: true,
        hasExpired: false,
        wasPaying: true,
        isTrialing: false,
        needsUpgrade: false,
        message: "Din prenumeration är aktiv.",
        callToAction: "Hantera prenumeration",
      };

    case "trialing":
      if (cancelAtPeriodEnd) {
        return {
          type: SubscriptionType.SCHEDULED_CANCELLATION,
          isActive: true,
          hasExpired: false,
          wasPaying: false,
          isTrialing: true,
          needsUpgrade: false,
          message:
            "Din testperiod kommer att avslutas utan att gå över till betald prenumeration.",
          callToAction: "Hantera prenumeration",
        };
      }
      return {
        type: SubscriptionType.ACTIVE_TRIAL,
        isActive: true,
        hasExpired: false,
        wasPaying: false,
        isTrialing: true,
        needsUpgrade: false,
        message: "Du är för närvarande i testperioden.",
        callToAction: "Hantera prenumeration",
      };

    case "canceled":
      if (hasExpired) {
        // Determine if they were paying or just trialing
        if (wasPaying) {
          return {
            type: SubscriptionType.CANCELED_PAYING,
            isActive: false,
            hasExpired: true,
            wasPaying: true,
            isTrialing: false,
            needsUpgrade: true,
            message: "Din prenumeration har upphört. Välkommen tillbaka!",
            callToAction: "Förnya prenumeration",
          };
        } else {
          return {
            type: SubscriptionType.EXPIRED_TRIAL,
            isActive: false,
            hasExpired: true,
            wasPaying: false,
            isTrialing: false,
            needsUpgrade: true,
            message:
              "För att fortsätta använda plattformen behöver du aktivera din prenumeration.",
            callToAction: "Gör det här",
          };
        }
      }
      // Canceled but still within period (still has access but can't restart from portal)
      // User needs to create a new subscription via checkout
      return {
        type: SubscriptionType.SCHEDULED_CANCELLATION,
        isActive: true, // Still has access until period end
        hasExpired: false,
        wasPaying: wasPaying,
        isTrialing: isCurrentlyTrialing,
        needsUpgrade: true, // Changed to true - they'll need to renew
        message: wasPaying
          ? "Din prenumeration är avslutad. Du har tillgång till periodens slut."
          : "Din testperiod är avslutad. Du har tillgång till periodens slut.",
        callToAction: "Förnya prenumeration",
      };

    case "past_due":
      return {
        type: SubscriptionType.PAST_DUE,
        isActive: false,
        hasExpired: false,
        wasPaying: true,
        isTrialing: false,
        needsUpgrade: true,
        message: "Din betalning har misslyckats. Uppdatera din betalmetod.",
        callToAction: "Hantera prenumeration",
      };

    case "incomplete":
      return {
        type: SubscriptionType.INCOMPLETE,
        isActive: false,
        hasExpired: false,
        wasPaying: false,
        isTrialing: false,
        needsUpgrade: true,
        message:
          "Din prenumeration är inte komplett. Lägg till en betalmetod för att aktivera.",
        callToAction: "Hantera prenumeration",
      };

    case "incomplete_expired":
      return {
        type: SubscriptionType.INCOMPLETE,
        isActive: false,
        hasExpired: true,
        wasPaying: false,
        isTrialing: false,
        needsUpgrade: true,
        message:
          "Din testperiod har gått ut. Lägg till en betalmetod för att fortsätta.",
        callToAction: "Hantera prenumeration",
      };

    case "unpaid":
      return {
        type: SubscriptionType.UNPAID,
        isActive: false,
        hasExpired: false,
        wasPaying: true,
        isTrialing: false,
        needsUpgrade: true,
        message: "Din prenumeration är obetald. Uppdatera din betalmetod.",
        callToAction: "Hantera prenumeration",
      };

    case "paused":
      return {
        type: SubscriptionType.PAUSED,
        isActive: false,
        hasExpired: false,
        wasPaying: false,
        isTrialing: false,
        needsUpgrade: true,
        message:
          "Din prenumeration är pausad. Förnya din prenumeration för att fortsätta använda alla funktioner.",
        callToAction: "Hantera prenumeration",
      };

    default:
      return {
        type: SubscriptionType.NO_SUBSCRIPTION,
        isActive: false,
        hasExpired: false,
        wasPaying: false,
        isTrialing: false,
        needsUpgrade: true,
        message: "Okänd prenumerationsstatus.",
        callToAction: "Kontakta support",
      };
  }
}

/**
 * Quick check if user has active access to features
 */
export function hasActiveSubscription(
  subscription: SubscriptionSummary | null | undefined
): boolean {
  const info = getSubscriptionInfo(subscription);
  return info.isActive;
}

/**
 * Check if user needs to upgrade/renew
 */
export function needsSubscriptionUpgrade(
  subscription: SubscriptionSummary | null | undefined
): boolean {
  const info = getSubscriptionInfo(subscription);
  return info.needsUpgrade;
}

/**
 * Get user-friendly status label
 */
export function getSubscriptionStatusLabel(
  subscription: SubscriptionSummary | null | undefined
): string {
  const info = getSubscriptionInfo(subscription);
  return info.message;
}

/**
 * Get appropriate button text for subscription action
 */
export function getSubscriptionActionLabel(
  subscription: SubscriptionSummary | null | undefined
): string {
  const info = getSubscriptionInfo(subscription);
  return info.callToAction;
}

/**
 * Check if account is a legacy account (pre-Stripe integration)
 * Legacy accounts have accountType of "pro", "premium", or "unlimited" WITHOUT a subscription object
 * New Stripe accounts also have accountType="pro" but WITH a subscription object
 */
export function isLegacyAccount(
  accountType: string | undefined,
  subscription: SubscriptionSummary | null | undefined
): boolean {
  const legacyAccountTypes = ["pro", "premium", "unlimited"];
  return legacyAccountTypes.includes(accountType || "") && !subscription;
}
